import { useSearchParams } from "react-router-dom";
import Breadcrumb from "../../../components/Breadcrumbs/Breadcrumb.tsx";
import SelectGroup from "../../../components/Forms/SelectGroup/SelectGroup.tsx";
import SectionHeader from "../../../components/SectionHeader/SectionHeader.tsx";
import { translateError } from "../../../constants/errorMessages.ts";
import { inputClass, labelClass } from "../../../constants/formStyles.ts";
import { insurerShort } from "../../../constants/insurers.ts";
import { useAuth } from "../../../provider/AuthProvider.tsx";
import { LifecycleError } from "../../../services/Integrations/LifecycleService.ts";
import type { PolicyValuesObject } from "../../../types/insurance.ts";
import type {
  LocalPaymentMethod,
  ProviderId,
} from "../../../types/lifecycle.ts";
import { BlankPicker } from "../Lifecycle/components/BlankPicker.tsx";
import { NoticeBox } from "../Lifecycle/components/Feedback.tsx";
import { CARD, InfoRow, Spinner } from "../Lifecycle/components/shared.tsx";
import { displayDate } from "../Lifecycle/dates.ts";
import { PAYMENT_METHODS } from "../Lifecycle/paymentMethods.ts";
import type { Notice } from "../Lifecycle/useLifecycle.ts";
import {
  type InstallmentPaymentController,
  isLocallyUnpaid,
  useInstallmentPayment,
} from "./useInstallmentPayment.ts";

/** An installment in full - amount, 2% tax and sticker fee - as the insurer states it. */
const installmentTotal = (row: PolicyValuesObject) =>
  Number(row.installmentAmount || 0) +
  Number(row.twoPercentAmount || 0) +
  Number(row.stickerAmount || 0);

const STATUS_LABELS: Record<string, string> = {
  due: "Дължима",
  paid: "Платена",
};

const errorNotice = (error: unknown): Notice => {
  if (error instanceof LifecycleError)
    return {
      kind: "error",
      text:
        error.code === "POLICY_REFERENCE_NOT_FOUND"
          ? "Полицата не е издадена през системата и вноските ѝ не могат да се платят оттук."
          : translateError(error.code, error.message).text,
      detail: [
        error.code,
        error.message,
        ...error.fields.map(
          (field) => `${field.field}: ${field.message ?? field.code}`
        ),
      ].join("\n"),
    };
  return {
    kind: "error",
    text:
      error instanceof Error && error.message
        ? error.message
        : "Възникна грешка при заявката.",
  };
};

const blankHint = (provider: ProviderId) =>
  provider === "ozk"
    ? "Изберете нов стикер и нов сертификат „Зелена карта“ за платения период."
    : provider === "levins"
    ? "Стикерът е задължителен за отчитане на плащане."
    : provider === "generali"
    ? "Бланки по избор: попълнете стикер, серия и номер на сертификат заедно."
    : "Бланки за плащането (по избор).";

function PaymentResult({
  controller,
}: {
  controller: InstallmentPaymentController;
}) {
  const {
    operationState: state,
    lastPayment,
    retryLocalWrite,
    canPrintReceipt,
    printReceipt,
    printing,
    printError,
  } = controller;
  const error = state.error;
  const retry = state.localWriteRetry;
  // Shown for as long as the accepted payment is unrecorded: it blocks every
  // other payment, whichever policy is loaded and whatever the last error was.
  if (retry) {
    const writeFailed =
      error instanceof LifecycleError && error.code === "LOCAL_PAYMENT_WRITE_FAILED";
    return (
      <div className="space-y-2">
        <NoticeBox
          notice={{
            kind: "warning",
            text: `Застрахователят прие плащането по полица ${
              retry.request.policyNumber
            }${
              retry.receiptNumber ? ` (разписка ${retry.receiptNumber})` : ""
            }, но вноската не е отбелязана в системата. Не плащайте отново — запишете я.`,
            detail: writeFailed ? error.message : undefined,
          }}
        />
        {Boolean(error) && !writeFailed && (
          <NoticeBox notice={errorNotice(error)} />
        )}
        <button
          type="button"
          onClick={() => void retryLocalWrite()}
          disabled={state.busy}
          className="rounded bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          Запиши вноската отново
        </button>
      </div>
    );
  }
  if (error) return <NoticeBox notice={errorNotice(error)} />;
  if (!lastPayment) return null;
  const { response } = lastPayment;
  return (
    <div className="space-y-2">
      <NoticeBox
        notice={{
          kind: "success",
          text: `Платена вноска${
            response.localWrite ? ` ${response.localWrite.installment}` : ""
          } по полица ${response.policy.policyNumber} — ${response.receipt.amount.eur.toFixed(
            2
          )} EUR, разписка ${
            response.receipt.receiptNumber ?? "—"
          }${
            response.receipt.greenCardNumber
              ? `, нов сертификат „Зелена карта“ ${response.receipt.greenCardNumber}`
              : ""
          }.`,
        }}
      />
      {canPrintReceipt && (
        <button
          type="button"
          onClick={() => void printReceipt()}
          disabled={printing}
          className="flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {printing && <Spinner />}
          Изтегли сметката и сертификата „Зелена карта“ (PDF)
        </button>
      )}
      {Boolean(printError) && <NoticeBox notice={errorNotice(printError)} />}
      {/* The payment is recorded even when, for example, the green card was refused. */}
      {response.advisories.length > 0 && (
        <NoticeBox
          notice={{
            // OZK confirms each recorded blank with an INFO note.
            kind: response.advisories.every(
              (advisory) => advisory.severity === "INFO"
            )
              ? "success"
              : "warning",
            text: `Бележки от застрахователя: ${response.advisories
              .map((advisory) => advisory.message)
              .join("; ")}`,
            detail: response.advisories.map((advisory) => advisory.code).join(", "),
          }}
        />
      )}
    </div>
  );
}

function PaymentsContent({
  jwt,
  headOff,
  provider,
  initialPolicyNumber,
}: {
  jwt: string;
  headOff: string;
  provider: ProviderId;
  initialPolicyNumber: string;
}) {
  const controller = useInstallmentPayment(
    jwt,
    headOff,
    provider,
    initialPolicyNumber
  );
  const {
    policyNumber,
    setPolicyNumber,
    search,
    searching,
    searchError,
    loaded,
    nextRow,
    localNext,
    mismatch,
    insurerDue,
    localUnpaid,
    capability,
    can,
    canPay,
    pay,
    refreshFailed,
    operationState: state,
    blankForm,
    paymentMethod,
    setPaymentMethod,
    offices,
    officeId,
    setOfficeId,
    agents,
    agentId,
    setAgentId,
  } = controller;
  const insurer = insurerShort(provider);
  const paymentSupported = can("installments.payment");
  return (
    <>
      <Breadcrumb pageName={`Вноски — ${insurer}`} />
      <div className="mx-auto w-full max-w-[1200px] space-y-4 p-4 lg:px-6">
        <fieldset className={CARD} disabled={searching || state.busy}>
          <SectionHeader title={`Плащане на вноска — ${insurer}`} />
          <form
            className="flex flex-wrap items-end gap-3 p-4 pt-2"
            onSubmit={(event) => {
              event.preventDefault();
              void search();
            }}
          >
            <div className="min-w-[260px] flex-1">
              <label htmlFor="payments-policy-number" className={labelClass}>
                Номер на полица
              </label>
              <input
                id="payments-policy-number"
                className={inputClass}
                value={policyNumber}
                onChange={(event) => setPolicyNumber(event.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={!policyNumber.trim()}
              className="flex items-center gap-1.5 rounded bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {searching && <Spinner />}
              Търси
            </button>
          </form>
          {capability && !paymentSupported && (
            <p className="px-4 pb-4 text-sm text-warning">
              Плащането на вноски към {insurer} още не е налично.
            </p>
          )}
        </fieldset>

        {Boolean(searchError) && <NoticeBox notice={errorNotice(searchError)} />}
        <PaymentResult controller={controller} />
        {refreshFailed && (
          <NoticeBox
            notice={{
              kind: "warning",
              text: "Плащането е потвърдено, но вноските не се обновиха. Потърсете полицата отново.",
            }}
          />
        )}

        {loaded && (
          <fieldset className={CARD} disabled={state.busy}>
            <SectionHeader title={`Полица ${loaded.policy.policyNumber}`} />
            <div className="space-y-4 p-4 pt-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                <InfoRow label="Застраховател" value={insurer} />
                <InfoRow label="Номер" value={loaded.policy.policyNumber} />
                <InfoRow
                  label="Зелена карта"
                  value={loaded.policy.greenCardNumber}
                />
              </div>

              <table className="w-full text-sm">
                <caption className="mb-1 text-left text-xs font-semibold">
                  Вноски при застрахователя
                  {loaded.schedule.completeness === "unpaid-only" &&
                    " (само неплатените)"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="text-left">Вноска</th>
                    <th scope="col" className="text-left">Падеж</th>
                    <th scope="col" className="text-left">Сума</th>
                    <th scope="col" className="text-right">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.schedule.installments.map((row) => (
                    <tr
                      key={`${row.sequence}-${row.dueDate}`}
                      className={`border-t border-stroke dark:border-strokedark ${
                        row === nextRow ? "font-semibold" : ""
                      }`}
                    >
                      <td className="py-2">{row.sequence}</td>
                      <td>{displayDate(row.dueDate)}</td>
                      <td>
                        {row.amount.eur.toFixed(2)} EUR /{" "}
                        {row.amount.bgn.toFixed(2)} лв.
                      </td>
                      <td className="text-right">
                        {STATUS_LABELS[row.status] ?? row.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loaded.schedule.installments.length === 0 && (
                <p className="text-sm">Няма върнати вноски.</p>
              )}

              <table className="w-full text-sm">
                <caption className="mb-1 text-left text-xs font-semibold">
                  Вноски в системата
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="text-left">Вноска</th>
                    <th scope="col" className="text-left">Падеж</th>
                    <th scope="col" className="text-left">Сума</th>
                    <th scope="col" className="text-right">Платена</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.installments.map((row) => (
                    <tr
                      key={row.installmentSequence}
                      className={`border-t border-stroke dark:border-strokedark ${
                        row === localNext ? "font-semibold" : ""
                      }`}
                    >
                      <td className="py-2">{row.installmentSequence}</td>
                      <td>{row.installmentDate ?? "—"}</td>
                      <td>{installmentTotal(row).toFixed(2)}</td>
                      <td className="text-right">
                        {isLocallyUnpaid(row) ? "Не" : row.reportDate ?? "Да"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {mismatch && (
                <NoticeBox
                  notice={{
                    kind: "warning",
                    text: `Застрахователят показва ${insurerDue} неплатени вноски, а системата — ${localUnpaid}. Плащането е спряно, за да не се отбележи грешна вноска; проверете полицата.`,
                  }}
                />
              )}

              {paymentSupported && nextRow && localNext && !mismatch && (
                <div className="space-y-3 rounded-sm bg-gray-2 p-3 dark:bg-meta-4">
                  <p className="text-sm">
                    Следваща вноска: падеж {displayDate(nextRow.dueDate)},{" "}
                    {nextRow.amount.eur.toFixed(2)} EUR. Плащането ще бъде
                    отбелязано на вноска {localNext.installmentSequence}
                    {localNext.installmentDate
                      ? ` (падеж ${localNext.installmentDate})`
                      : ""}
                    .
                  </p>
                  {provider !== "dzi" && (
                    <div className="space-y-2">
                      {/* Wing holds blanks per insurer agency and agent. The
                          agency is the policy's; its VALIORS triggers match it. */}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div>
                          <label htmlFor="payments-agency" className={labelClass}>
                            Агенция на застрахователя
                          </label>
                          <SelectGroup
                            id="payments-agency"
                            value={loaded.localCid}
                            options={{ [loaded.localCid]: loaded.agencyName }}
                            disabled
                          />
                          <p className="mt-1 text-xs">
                            Агенцията, към която е записана полицата.
                          </p>
                        </div>
                        <div>
                          <label htmlFor="payments-agent" className={labelClass}>
                            Агент
                          </label>
                          <SelectGroup
                            id="payments-agent"
                            value={agentId}
                            onChange={(value) => setAgentId(String(value))}
                            options={agents}
                            placeholder="— изберете —"
                          />
                        </div>
                      </div>
                      <p className="text-sm">{blankHint(provider)}</p>
                      <BlankPicker form={blankForm} />
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <label htmlFor="payments-method" className={labelClass}>
                        Плащане от клиента
                      </label>
                      <SelectGroup
                        id="payments-method"
                        value={paymentMethod}
                        onChange={(value) =>
                          setPaymentMethod(String(value) as LocalPaymentMethod)
                        }
                        options={PAYMENT_METHODS}
                      />
                    </div>
                    {paymentMethod === "cash" && (
                      <div>
                        <label htmlFor="payments-office" className={labelClass}>
                          Офис
                        </label>
                        <SelectGroup
                          id="payments-office"
                          value={officeId}
                          onChange={(value) => setOfficeId(String(value))}
                          options={offices}
                          placeholder="— изберете —"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => void pay()}
                    disabled={!canPay}
                    className="flex items-center gap-1.5 rounded bg-success px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {state.busy && <Spinner />}
                    Плати — {nextRow.amount.eur.toFixed(2)} EUR
                  </button>
                </div>
              )}
              {!nextRow && (
                <p className="text-sm">Няма дължими вноски при застрахователя.</p>
              )}
            </div>
          </fieldset>
        )}
      </div>
    </>
  );
}

function InstallmentPayments({ provider }: { provider: ProviderId }) {
  const { jwt, userID, headOff } = useAuth();
  const [params] = useSearchParams();
  if (!jwt || !userID || !headOff) return null;
  return (
    <PaymentsContent
      key={`${headOff}|${userID}|${provider}`}
      jwt={jwt}
      headOff={headOff}
      provider={provider}
      initialPolicyNumber={params.get("policy") ?? ""}
    />
  );
}

export const OzkPayments = () => <InstallmentPayments provider="ozk" />;
export const DziPayments = () => <InstallmentPayments provider="dzi" />;
export const GeneraliPayments = () => <InstallmentPayments provider="generali" />;
export const LevInsPayments = () => <InstallmentPayments provider="levins" />;
